import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { BACKOFFICE_PLAYER_URL } from '../../flagged_chats/ThreadRow';
import { Badge } from '../../ui/badge';

// Account flags worth a manager's attention before granting a comp; shown only when set.
const FLAGS = [
  ['is_test', 'Test account', 'destructive'],
  ['disabled', 'Disabled', 'destructive'],
  ['suspended', 'Suspended', 'destructive'],
  ['is_do_not_pay', 'Do not pay', 'destructive'],
  ['is_do_not_pay_soft', 'Do not pay (soft)', 'warning'],
  ['is_suspicious', 'Suspicious', 'warning'],
  ['is_escalated', 'Escalated', 'warning'],
  ['is_kyc_required', 'KYC required', 'warning'],
];

// backofficePlayerUrl: the player's page in the brand's back office (BitStarz when the brand has none).
export const backofficePlayerUrl = (brand, userId) =>
  `${BACKOFFICE_PLAYER_URL[brand] || BACKOFFICE_PLAYER_URL.bitstarz}/${encodeURIComponent(userId)}`;

const LINK = 'flex items-center gap-1.5 text-sm font-bold text-brand-blue hover:underline';

// PlayerIdentity is the player row both views start with: initial, email with VIP / verified /
// account-flag badges, the IDs, and the back office and profile links — vertically centred.
export default function PlayerIdentity({ player, backofficeUrl }) {
  const name = [player.first_name, player.last_name].filter(Boolean).join(' ');
  const flags = FLAGS.filter(([key]) => player[key]);
  const initial = (player.email || player.nickname || String(player.user_id)).charAt(0).toUpperCase();
  return (
    <div className="flex flex-wrap items-center gap-3.5">
      <div aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-surface-2 font-black text-ink-dim">{initial}</div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-base font-bold text-ink">{player.email || player.user_id}</h2>
          {player.is_vip && <Badge variant="success">VIP</Badge>}
          {player.is_verified && <Badge variant="secondary">Verified</Badge>}
          {flags.map(([key, label, variant]) => <Badge key={key} variant={variant}>{label}</Badge>)}
        </div>
        <div className="text-xs font-medium text-ink-dim">
          ID {player.user_id}{name ? ` · ${name}` : ''}{player.nickname ? ` · ${player.nickname}` : ''}
        </div>
      </div>
      <div className="flex items-center gap-4">
        <a href={backofficeUrl} target="_blank" rel="noopener noreferrer" title="Open the player in the back office" className={LINK}>
          Back office <ExternalLink className="h-4 w-4" />
        </a>
        <Link to={`/player/${player.user_id}?from=dashboard`} className={LINK}>
          Player profile <ExternalLink className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
